import React from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/system";
import variables from "theme/styles/_variables.module.scss";

type ChartPieSkeletonProps = {
  minHeight?: number;
};

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 0.5; }
  100% { opacity: 0.3; }
`;

const ChartPieSkeleton: React.FC<ChartPieSkeletonProps> = (props) => {
  const minHeight = props.minHeight ?? 200;

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: minHeight,
        minHeight,
        p: { xs: 1, sm: 2 },
        gap: { xs: 2, sm: 3 },
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      {/* Doughnut chart skeleton */}
      <Box
        sx={{
          flex: { xs: "0 1 auto", sm: "0 1 40%" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          minHeight: { xs: 120, sm: 160 },
        }}
      >
        {/* Container for maintaining aspect ratio */}
        <Box
          sx={{
            position: "relative",
            width: { xs: "120px", sm: "min(160px, 100%)" },
            height: { xs: "120px", sm: "min(160px, 100%)" },
            aspectRatio: "1 / 1",
          }}
        >
          {/* Animated ring */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              borderWidth: { xs: "20px", sm: "30px" },
              borderStyle: "solid",
              borderColor: variables.skeletonPaper,
              borderTopColor: variables.lightGray,
              animation: `${rotate} 2s linear infinite`,
            }}
          />
          {/* Inner circle */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "60%", sm: "62.5%" },
              height: { xs: "60%", sm: "62.5%" },
              borderRadius: "50%",
              backgroundColor: variables.lightGrayV3,
              animation: `${pulse} 2s ease-in-out infinite`,
            }}
          />
        </Box>
      </Box>

      {/* Legend skeleton */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: { xs: 1, sm: 2 },
          minWidth: 0,
        }}
      >
        {[1, 2, 3, 4, 5].map((idx) => (
          <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
            {/* Color indicator */}
            <Box
              sx={{
                flexShrink: 0,
                width: { xs: 10, sm: 12 },
                height: { xs: 10, sm: 12 },
                borderRadius: "50%",
                backgroundColor: variables.skeletonPaper,
                animation: `${pulse} 2s ease-in-out ${idx * 0.2}s infinite`,
              }}
            />
            {/* Label */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: "100%",
                height: { xs: 10, sm: 12 },
                borderRadius: 1,
                backgroundColor: variables.skeletonPaper,
                animation: `${pulse} 2s ease-in-out ${idx * 0.2}s infinite`,
              }}
            />
            {/* Value */}
            <Box
              sx={{
                marginLeft: "auto",
                flexShrink: 0,
                width: { xs: 35, sm: 50 },
                height: { xs: 10, sm: 12 },
                borderRadius: 1,
                backgroundColor: variables.skeletonPaper,
                animation: `${pulse} 2s ease-in-out ${idx * 0.2}s infinite`,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ChartPieSkeleton;
